import { useEffect, useState } from "react";
import { api, cadastroClienteUrl, buildSistemaWhatsappMessage, openWhatsapp } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, X, Link as LinkIcon, MessageCircle } from "lucide-react";

const empty = { nome: "", endereco: "", whatsapp: "", email: "", observacoes: "" };

// Compose full WhatsApp number: user only types the local part (DDD 11).
// If they type digits with country code (starts with 55), we accept it.
const composeWpp = (local) => {
  const digits = (local || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.startsWith("55") && digits.length >= 12) return digits;
  return `5511${digits}`;
};
const stripWppPrefix = (full) => {
  const digits = (full || "").replace(/\D/g, "");
  if (digits.startsWith("5511")) return digits.slice(4);
  if (digits.startsWith("55")) return digits.slice(2);
  return digits;
};

export default function Clientes() {
  const [clientes, setClientes] = useState([]);
  const [q, setQ] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [wppLocal, setWppLocal] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const { data } = await api.get("/clientes", { params: q ? { q } : {} });
    setClientes(data);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const openNew = () => {
    setEditing(null);
    setForm(empty);
    setWppLocal("");
    setShowForm(true);
  };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      nome: c.nome || "",
      endereco: c.endereco || "",
      whatsapp: c.whatsapp || "",
      email: c.email || "",
      observacoes: c.observacoes || "",
    });
    setWppLocal(stripWppPrefix(c.whatsapp));
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome.trim()) {
      toast.error("Informe o nome do cliente");
      return;
    }
    setLoading(true);
    const payload = { ...form, whatsapp: composeWpp(wppLocal) };
    try {
      if (editing) {
        await api.put(`/clientes/${editing.id}`, payload);
        toast.success("Cliente atualizado");
      } else {
        await api.post("/clientes", payload);
        toast.success("Cliente cadastrado");
      }
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar");
    } finally {
      setLoading(false);
    }
  };

  const del = async (c) => {
    if (!window.confirm(`Excluir cliente ${c.codigo_cliente} - ${c.nome}?`)) return;
    try {
      await api.delete(`/clientes/${c.id}`);
      toast.success("Cliente excluído");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao excluir");
    }
  };

  const copiarLinkCadastro = async (c) => {
    if (!c.codigo_cadastro) return toast.error("Cliente sem código de cadastro. Salve novamente.");
    const url = cadastroClienteUrl(c.codigo_cadastro);
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link de cadastro copiado!");
    } catch {
      toast.info(url);
    }
  };

  const enviarLinkWhatsapp = (c) => {
    if (!c.codigo_cadastro) return toast.error("Cliente sem código de cadastro. Salve novamente.");
    if (!c.whatsapp) return toast.error("Cliente sem WhatsApp cadastrado.");
    const url = cadastroClienteUrl(c.codigo_cadastro);
    const msg = buildSistemaWhatsappMessage([
      `Olá ${c.nome}, acesse seu portal para acompanhar seus orçamentos.`,
      "",
      "Link para cadastro no portal:",
      url,
    ]);
    openWhatsapp(c.whatsapp, msg);
  };

  return (
    <div className="space-y-6" data-testid="clientes-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500 mb-1">
            Cadastro
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Clientes</h1>
        </div>
        <button
          onClick={openNew}
          data-testid="novo-cliente-btn"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bbh-btn-primary text-sm font-semibold self-start"
        >
          <Plus className="w-4 h-4" /> Novo Cliente
        </button>
      </div>

      <div className="bbh-card p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <input
            data-testid="clientes-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nome, código, email, WhatsApp..."
            className="w-full pl-9 pr-3 py-2.5 rounded-md border border-zinc-200 bbh-input text-sm bg-white"
          />
        </div>
      </div>

      <div className="bbh-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-xs uppercase tracking-wide text-zinc-500">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Código</th>
              <th className="text-left px-4 py-3 font-semibold">Nome</th>
              <th className="text-left px-4 py-3 font-semibold hidden md:table-cell">Contato</th>
              <th className="text-left px-4 py-3 font-semibold hidden lg:table-cell">Email</th>
              <th className="text-right px-4 py-3 font-semibold">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {clientes.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center py-10 text-zinc-500">
                  Nenhum cliente cadastrado
                </td>
              </tr>
            )}
            {clientes.map((c) => (
              <tr key={c.id} className="bbh-row" data-testid={`cliente-row-${c.codigo_cliente}`}>
                <td className="px-4 py-3 font-mono font-semibold">{c.codigo_cliente}</td>
                <td className="px-4 py-3">{c.nome}</td>
                <td className="px-4 py-3 hidden md:table-cell text-zinc-600">
                  {c.whatsapp || "-"}
                </td>
                <td className="px-4 py-3 hidden lg:table-cell text-zinc-600">{c.email || "-"}</td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-1 items-center">
                    {c.ja_cadastrado && (
                      <span
                        title="Cliente já tem conta no portal"
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-semibold"
                        data-testid={`cliente-cadastrado-${c.codigo_cadastro || c.id}`}
                      >
                        Portal ✓
                      </span>
                    )}
                    {!c.ja_cadastrado && (
                      <>
                        <button
                          onClick={() => copiarLinkCadastro(c)}
                          title="Copiar link de cadastro"
                          className="p-2 rounded hover:bg-zinc-100 text-zinc-700"
                          data-testid={`copy-cadastro-${c.codigo_cadastro || c.id}`}
                        >
                          <LinkIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => enviarLinkWhatsapp(c)}
                          title="Enviar link de cadastro por WhatsApp"
                          className="p-2 rounded hover:bg-emerald-50 text-emerald-600"
                          data-testid={`wpp-cadastro-${c.codigo_cadastro || c.id}`}
                        >
                          <MessageCircle className="w-4 h-4" />
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => openEdit(c)}
                      className="p-2 rounded hover:bg-zinc-100"
                      data-testid={`edit-cliente-${c.codigo_cliente}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => del(c)}
                      className="p-2 rounded hover:bg-rose-50 text-rose-600"
                      data-testid={`delete-cliente-${c.codigo_cliente}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-xl w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-zinc-200 sticky top-0 bg-white">
              <h2 className="font-heading text-xl font-semibold">
                {editing ? `Editar ${editing.codigo_cliente}` : "Novo Cliente"}
              </h2>
              <button onClick={() => setShowForm(false)} data-testid="close-cliente-form">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={submit} className="p-5 space-y-4">
              <Field label="Nome *" testid="cliente-nome" value={form.nome}
                onChange={(v) => setForm({ ...form, nome: v })} />
              <Field label="Endereço" testid="cliente-endereco" value={form.endereco}
                onChange={(v) => setForm({ ...form, endereco: v })} />
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">
                  WhatsApp
                </label>
                <div className="mt-1 flex rounded-md border border-zinc-200 bg-white overflow-hidden focus-within:border-[#14243D] focus-within:ring-2 focus-within:ring-[#14243D]/15">
                  <span className="px-3 py-2 bg-zinc-50 border-r border-zinc-200 text-sm text-zinc-500 select-none font-mono">
                    +55 11
                  </span>
                  <input
                    data-testid="cliente-whatsapp"
                    type="tel"
                    value={wppLocal}
                    placeholder="99999-9999"
                    onChange={(e) => setWppLocal(e.target.value.replace(/[^\d\s-]/g, ""))}
                    className="flex-1 px-3 py-2 text-sm outline-none"
                  />
                </div>
                <div className="text-[10px] text-zinc-400 mt-1">Digite apenas o número; o prefixo +55 11 é adicionado automaticamente.</div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">
                  Observações
                </label>
                <textarea
                  data-testid="cliente-observacoes"
                  value={form.observacoes}
                  onChange={(e) => setForm({ ...form, observacoes: e.target.value })}
                  rows={3}
                  className="mt-1 w-full px-3 py-2 rounded-md border border-zinc-200 bbh-input text-sm"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="flex-1 px-4 py-2.5 rounded-md border border-zinc-200 font-medium"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="salvar-cliente-btn"
                  className="flex-1 px-4 py-2.5 rounded-md bbh-btn-primary font-semibold disabled:opacity-50"
                >
                  {loading ? "Salvando..." : "Salvar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text", placeholder }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">
        {label}
      </label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full px-3 py-2 rounded-md border border-zinc-200 bbh-input text-sm bg-white"
      />
    </div>
  );
}
